import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ArrowLeft, Navigation, Route, AlertTriangle, Loader2, Phone, Printer } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatJMD, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";

const pin = (label, color) =>
  L.divIcon({
    className: "",
    html: `<div style="width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${color};border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center"><span style="transform:rotate(45deg);color:#fff;font-weight:800;font-size:12px;font-family:sans-serif">${label}</span></div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -28],
  });

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length > 1) map.fitBounds(points, { padding: [40, 40] });
    else if (points.length === 1) map.setView(points[0], 14);
  }, [points, map]);
  return null;
};

export default function RouteMapPage() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) return;
    api.get(`/deliveries/${runId}/route`).then((r) => setData(r.data)).catch(() => setError("Could not build route"));
  }, [user, runId]);

  if (loading) return null;
  if (!user) { navigate("/admin"); return null; }

  if (error) return <div className="p-10 text-rose-600" data-testid="route-error">{error}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500 gap-2" data-testid="route-loading"><Loader2 className="w-5 h-5 animate-spin" /> Geocoding stops & planning route…</div>;

  const depotPt = [data.depot.lat, data.depot.lng];
  const stopPts = data.stops.map((s) => [s.lat, s.lng]);
  const path = [depotPt, ...stopPts];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="route-map-page">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-[1000]">
        <div className="max-w-7xl mx-auto px-5 h-14 flex items-center justify-between gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="route-back"><ArrowLeft className="w-4 h-4 mr-1" /> Logistics</Button>
          <div className="text-sm text-slate-600 hidden sm:block">
            <strong className="text-slate-900">{data.run.driver}</strong> · {fmtDate(data.run.run_date)} · {data.stops.length} mapped stop{data.stops.length === 1 ? "" : "s"} · <span data-testid="route-distance">~{data.distance_km} km</span>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => navigate(`/admin/manifest/${runId}`)} data-testid="route-run-sheet" className="rounded-xl"><Printer className="w-4 h-4 mr-1" /> Run sheet</Button>
            {data.google_maps_url && (
              <a href={data.google_maps_url} target="_blank" rel="noreferrer" data-testid="route-open-gmaps">
                <Button size="sm" className="rounded-xl bg-sky-600 hover:bg-sky-500 text-white"><Navigation className="w-4 h-4 mr-1" /> Navigate in Google Maps</Button>
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 grid lg:grid-cols-[1fr_380px]">
        <div className="h-[55vh] lg:h-auto min-h-[400px]" data-testid="route-map">
          <MapContainer center={depotPt} zoom={12} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FitBounds points={path} />
            <Polyline positions={path} pathOptions={{ color: "#0284c7", weight: 4, opacity: 0.8, dashArray: "8 10" }} />
            <Marker position={depotPt} icon={pin("D", "#0f172a")}><Popup><strong>Depot</strong><br />{data.depot.label}</Popup></Marker>
            {data.stops.map((s, i) => (
              <Marker key={s.order_id} position={[s.lat, s.lng]} icon={pin(i + 1, s.payment_method === "cod" ? "#059669" : "#0284c7")}>
                <Popup>
                  <strong>#{i + 1} {s.customer_name}</strong><br />{s.address}<br />{s.phone}<br />
                  {s.items.map((it) => `${it.quantity}× ${it.product_name}`).join(", ")}<br />
                  <strong>{formatJMD(s.total_amount)}</strong> · {s.payment_method === "cod" ? "Collect cash" : "Bank transfer"}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        <aside className="bg-white border-l border-slate-200 overflow-y-auto p-5" data-testid="route-stops">
          <h3 className="font-display font-bold text-slate-900 flex items-center gap-2"><Route className="w-5 h-5 text-sky-600" /> Suggested order</h3>
          <p className="text-xs text-slate-500 mt-1">Nearest-stop-first from the depot. Green pins collect cash.</p>
          <ol className="mt-4 space-y-2">
            <li className="flex gap-3 items-start text-sm"><span className="w-7 h-7 rounded-full bg-slate-900 text-white font-bold flex items-center justify-center text-xs shrink-0">D</span><div><div className="font-semibold text-slate-800">Depot</div><div className="text-slate-500 text-xs">{data.depot.label}</div></div></li>
            {data.stops.map((s, i) => (
              <li key={s.order_id} className="flex gap-3 items-start text-sm rounded-xl p-2 hover:bg-slate-50" data-testid={`route-stop-${i + 1}`}>
                <span className={`w-7 h-7 rounded-full text-white font-bold flex items-center justify-center text-xs shrink-0 ${s.payment_method === "cod" ? "bg-emerald-600" : "bg-sky-600"}`}>{i + 1}</span>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800">{s.customer_name} <span className="font-mono-cw text-xs text-slate-400">{s.order_code}</span>{s.precision === "approximate" && <span className="ml-1 text-[10px] font-semibold text-amber-600 uppercase" title="Pinned to street/area, not exact house">≈ area</span>}</div>
                  <div className="text-slate-500 text-xs">{s.address}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" />{s.phone} · {formatJMD(s.total_amount)}</div>
                </div>
              </li>
            ))}
          </ol>
          {data.unmapped.length > 0 && (
            <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="route-unmapped">
              <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm"><AlertTriangle className="w-4 h-4" /> {data.unmapped.length} stop{data.unmapped.length > 1 ? "s" : ""} couldn't be mapped</div>
              <p className="text-xs text-amber-700 mt-1">Address not found — call the customer or add a landmark/parish to the address.</p>
              <ul className="mt-2 space-y-1 text-xs text-amber-900">
                {data.unmapped.map((s) => <li key={s.order_id}><strong>{s.customer_name}</strong> — {s.address} · {s.phone}</li>)}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
